import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Tournament, Match, Team, Sport } from '@/types';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /** Последний подтверждённый ответ GET /manager. Не сбрасывается, пока запрос в пути. */
  canManage: boolean;
  setCanManage: (canManage: boolean) => void;
  setAuthTokens: (accessToken: string | null, refreshToken?: string | null) => void;
  logout: () => void;
}

function readPersistedAuth(): Partial<Pick<AuthState, 'token' | 'refreshToken' | 'isAuthenticated' | 'canManage'>> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { state?: Partial<AuthState> };
    return parsed.state ?? {};
  } catch {
    return {};
  }
}

const persistedAuth = readPersistedAuth();

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: persistedAuth.token ?? null,
      refreshToken: persistedAuth.refreshToken ?? null,
      isAuthenticated: persistedAuth.isAuthenticated ?? false,
      canManage: persistedAuth.canManage ?? false,
      setCanManage: (canManage) => set((state) => (state.canManage === canManage ? state : { canManage })),
      setAuthTokens: (token, refreshToken = null) => {
        if (token) {
          localStorage.setItem('auth_token', token);
        } else {
          localStorage.removeItem('auth_token');
        }
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        } else {
          localStorage.removeItem('refresh_token');
        }
        set({
          token,
          refreshToken,
          isAuthenticated: !!token,
          ...(token ? {} : { canManage: false }),
        });
      },
      logout: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        set({ token: null, refreshToken: null, isAuthenticated: false, canManage: false });
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);

interface ThemeState {
  mode: 'light' | 'dark';
  toggleMode: () => void;
  setMode: (mode: 'light' | 'dark') => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'dark',
      toggleMode: () => set((state) => ({ mode: state.mode === 'light' ? 'dark' : 'light' })),
      setMode: (mode) => set({ mode }),
    }),
    {
      name: 'theme-storage',
    }
  )
);

interface FavoritesState {
  tournaments: string[];
  teams: string[];
  matches: string[];
  addTournament: (id: string) => void;
  removeTournament: (id: string) => void;
  addTeam: (id: string) => void;
  removeTeam: (id: string) => void;
  addMatch: (id: string) => void;
  removeMatch: (id: string) => void;
  isTournamentFavorite: (id: string) => boolean;
  isTeamFavorite: (id: string) => boolean;
  isMatchFavorite: (id: string) => boolean;
}

export const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      tournaments: [],
      teams: [],
      matches: [],
      addTournament: (id) => set((state) => ({
        tournaments: [...state.tournaments, id],
      })),
      removeTournament: (id) => set((state) => ({
        tournaments: state.tournaments.filter((t) => t !== id),
      })),
      addTeam: (id) => set((state) => ({
        teams: [...state.teams, id],
      })),
      removeTeam: (id) => set((state) => ({
        teams: state.teams.filter((t) => t !== id),
      })),
      addMatch: (id) => set((state) => ({
        matches: [...state.matches, id],
      })),
      removeMatch: (id) => set((state) => ({
        matches: state.matches.filter((m) => m !== id),
      })),
      isTournamentFavorite: (id) => get().tournaments.includes(id),
      isTeamFavorite: (id) => get().teams.includes(id),
      isMatchFavorite: (id) => get().matches.includes(id),
    }),
    {
      name: 'favorites-storage',
    }
  )
);

interface UIState {
  selectedSport: Sport | null;
  selectedTournament: Tournament | null;
  selectedMatch: Match | null;
  selectedTeam: Team | null;
  setSelectedSport: (sport: Sport | null) => void;
  setSelectedTournament: (tournament: Tournament | null) => void;
  setSelectedMatch: (match: Match | null) => void;
  setSelectedTeam: (team: Team | null) => void;
  /** Режим «Админ»: показывает управляющие элементы там, где есть права. */
  adminMode: boolean;
  setAdminMode: (adminMode: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      selectedSport: null,
      selectedTournament: null,
      selectedMatch: null,
      selectedTeam: null,
      setSelectedSport: (sport) => set({ selectedSport: sport }),
      setSelectedTournament: (tournament) => set({ selectedTournament: tournament }),
      setSelectedMatch: (match) => set({ selectedMatch: match }),
      setSelectedTeam: (team) => set({ selectedTeam: team }),
      adminMode: false,
      setAdminMode: (adminMode) => set({ adminMode }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({ adminMode: state.adminMode }),
    }
  )
);

interface LiveMatchesState {
  liveMatches: Match[];
  setLiveMatches: (matches: Match[]) => void;
  addLiveMatch: (match: Match) => void;
  removeLiveMatch: (matchId: string) => void;
  updateLiveMatch: (match: Match) => void;
}

export const useLiveMatchesStore = create<LiveMatchesState>()((set) => ({
  liveMatches: [],
  setLiveMatches: (matches) => set({ liveMatches: matches }),
  addLiveMatch: (match) => set((state) => ({
    liveMatches: [...state.liveMatches, match],
  })),
  removeLiveMatch: (matchId) => set((state) => ({
    liveMatches: state.liveMatches.filter((m) => m.id !== matchId),
  })),
  updateLiveMatch: (match) => set((state) => ({
    liveMatches: state.liveMatches.map((m) =>
      m.id === match.id ? match : m
    ),
  })),
}));
